// src/pages/AdminRevenue.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    collection,
    collectionGroup,
    onSnapshot,
    orderBy,
    query,
    where,
    Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import AdminHeader from "../components/AdminHeader";
import { Link, useLocation } from "react-router-dom";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    LineChart, Line, ResponsiveContainer,
} from "recharts";
import AdminSidebar from "../components/AdminSidebar";

const NEON = "#D4FF00";

// ---------- UI helpers ----------
function NeonCard({ title, children, right }) {
    return (
        <div className="relative overflow-visible rounded-2xl border border-[#D4FF00]/30 bg-black/60 p-4 shadow-[0_0_40px_rgba(212,255,0,0.1)]">
            <div className="flex items-center justify-between mb-2">
                {title ? <h3 className="text-[#D4FF00] font-semibold text-sm tracking-wide">{title}</h3> : <div />}
                {right}
            </div>
            {children}
        </div>
    );
}
function Stat({ label, value, sub }) {
    return (
        <div className="rounded-xl bg-neutral-950 border border-white/5 p-4">
            <div className="text-xs text-gray-400">{label}</div>
            <div className="text-2xl font-bold text-white">{value}</div>
            {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

// ---------- date helpers ----------
function toYMD(d) {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function endOfDay(d) { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }

function startOfISOWeek(date) {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7; // Mon=0..Sun=6
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
}
function endOfISOWeek(date) { const s = startOfISOWeek(date); return endOfDay(addDays(s, 6)); }
function isoWeek(d) {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
    return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}
function buildRangeKeys(granularity, start, end) {
    const keys = [];
    if (granularity === "day") {
        let cur = startOfDay(start), last = endOfDay(end);
        while (cur <= last) { keys.push(toYMD(cur)); cur = addDays(cur, 1); }
    } else if (granularity === "week") {
        let cur = startOfISOWeek(start), last = endOfISOWeek(end);
        while (cur <= last) { keys.push(isoWeek(cur)); cur = addDays(cur, 7); }
    } else {
        let cur = new Date(start.getFullYear(), start.getMonth(), 1);
        const last = new Date(end.getFullYear(), end.getMonth(), 1);
        while (cur <= last) { keys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`); cur = addMonths(cur, 1); }
    }
    return keys;
}
function periodBoundsForKey(key, granularity) {
    if (granularity === "day") {
        const d = new Date(key);
        return { s: startOfDay(d), e: endOfDay(d) };
    }
    if (granularity === "week") {
        const [yStr, wStr] = key.split("-W");
        const y = parseInt(yStr, 10); const w = parseInt(wStr, 10);
        const simple = new Date(Date.UTC(y, 0, 1 + (w - 1) * 7));
        const dow = simple.getUTCDay();
        if (dow <= 4) simple.setUTCDate(simple.getUTCDate() - dow + 1);
        else simple.setUTCDate(simple.getUTCDate() + 8 - dow);
        const s = new Date(simple); s.setHours(0, 0, 0, 0);
        const e = endOfDay(addDays(s, 6));
        return { s, e };
    }
    const [y, m] = key.split("-").map(Number);
    const s = new Date(y, m - 1, 1);
    const e = new Date(y, m, 0); e.setHours(23, 59, 59, 999);
    return { s, e };
}

// Build Date objects from top-level booking {date:"YYYY-MM-DD", startTime:"HH:mm", endTime:"HH:mm"}
function fromDateAndTimeStrings(dateStr, timeStr) {
    // Treat as local time
    const [H, M] = timeStr.split(":").map(Number);
    const d = new Date(dateStr);
    d.setHours(H, M, 0, 0);
    return d;
}

// ---------- Component ----------
export default function AdminRevenue() {
    const location = useLocation();

    // Default: this week (since you want “next week” a lot)
    const thisWeekStart = startOfISOWeek(new Date());
    const thisWeekEnd = endOfISOWeek(new Date());

    const [start, setStart] = useState(thisWeekStart);
    const [end, setEnd] = useState(thisWeekEnd);
    const [bookings, setBookings] = useState([]);        // normalized bookings with {start:Date,end:Date,status}
    const [loading, setLoading] = useState(true);
    const [errMsg, setErrMsg] = useState("");
    const [usingFallback, setUsingFallback] = useState(false);
    const [granularity, setGranularity] = useState("week");
    const [metric, setMetric] = useState("revenue");
    const [assumptions, setAssumptions] = useState({
        pcs: 5,
        pricePerHour: 8,
        hoursOpenPerDay: 8,
        daysPerMonth: 30,
        occupancyTarget: 0.55,
        snackARPU: 0,
        monthlyFixedCosts: 2500,
    });

    // Quick ranges
    function setThisWeek() {
        const s = startOfISOWeek(new Date());
        const e = endOfISOWeek(new Date());
        setStart(s); setEnd(e); setGranularity("week");
    }
    function setNextWeek() {
        const s = addDays(startOfISOWeek(new Date()), 7);
        const e = endOfISOWeek(s);
        setStart(s); setEnd(e); setGranularity("week");
    }
    function setNext7Days() {
        const s = startOfDay(new Date());
        const e = endOfDay(addDays(s, 6));
        setStart(s); setEnd(e); setGranularity("day");
    }
    function setNextMonth() {
        const now = new Date();
        const firstNext = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const lastNext = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        setStart(firstNext); setEnd(endOfDay(lastNext)); setGranularity("month");
    }

    // ---- BOOKINGS: try collectionGroup(users/*/bookings) → fallback to /bookings ----
    useEffect(() => {
        setErrMsg("");
        setLoading(true);
        setUsingFallback(false);

        if (!(start instanceof Date) || isNaN(+start) || !(end instanceof Date) || isNaN(+end)) {
            setBookings([]);
            setLoading(false);
            setErrMsg("Invalid date range.");
            return;
        }

        // Preferred: users/*/bookings with Timestamp fields (start/end)
        const s = startOfDay(start);
        const ePlus = new Date(endOfDay(end).getTime() + 1);

        const usersBookingsRef = query(
            collectionGroup(db, "bookings"),
            where("start", ">=", Timestamp.fromDate(s)),
            where("start", "<", Timestamp.fromDate(ePlus)),
            orderBy("start", "asc")
        );

        let unsubA = () => { };
        let unsubB = () => { };

        const tryFallback = () => {
            // Fallback: top-level /bookings with string fields (date, startTime, endTime)
            setUsingFallback(true);
            const topRef = query(
                collection(db, "bookings"),
                where("date", ">=", toYMD(start)),   // lexicographic range works for YYYY-MM-DD
                where("date", "<=", toYMD(end)),
                orderBy("date", "asc"),
                orderBy("startTime", "asc")
            );

            unsubB = onSnapshot(
                topRef,
                (snap) => {
                    const rows = snap.docs.map((d) => {
                        const data = d.data() || {};
                        const date = data.date; // "YYYY-MM-DD"
                        const startTime = data.startTime; // "HH:mm"
                        const endTime = data.endTime;     // "HH:mm"
                        const startDt = (date && startTime) ? fromDateAndTimeStrings(date, startTime) : new Date();
                        const endDt = (date && endTime) ? fromDateAndTimeStrings(date, endTime) : startDt;
                        return {
                            id: d.id,
                            status: (data.status || "").toLowerCase(),
                            start: startDt,
                            end: endDt,
                            pcId: data.pcId,
                        };
                    });
                    setBookings(rows);
                    setLoading(false);
                },
                (err) => {
                    console.error("fallback /bookings query failed", err);
                    setErrMsg(err?.message || "Failed to load bookings.");
                    setLoading(false);
                }
            );
        };

        unsubA = onSnapshot(
            usersBookingsRef,
            (snap) => {
                // Success: normalize users/*/bookings
                const rows = snap.docs.map((d) => {
                    const data = d.data() || {};
                    return {
                        id: d.id,
                        status: (data.status || "").toLowerCase(),
                        start: data.start?.toDate?.() || new Date(),
                        end: data.end?.toDate?.() || data.start?.toDate?.() || new Date(),
                        pcId: data.seatId || data.pcId, // tolerate both keys
                    };
                });
                setBookings(rows);
                setLoading(false);
            },
            (err) => {
                // Permission denied → fall back
                console.warn("users/*/bookings query failed — falling back to /bookings:", err?.message);
                tryFallback();
            }
        );

        return () => { unsubA(); unsubB(); };
    }, [start, end]);

    // Split bookings crossing midnight into daily pieces
    function splitBookingByDay(bStart, bEnd) {
        const clampedStart = new Date(Math.max(bStart.getTime(), startOfDay(start).getTime()));
        const clampedEnd = new Date(Math.min(bEnd.getTime(), endOfDay(end).getTime()));
        if (clampedEnd <= clampedStart) return [];

        const parts = [];
        let cur = clampedStart;
        while (cur < clampedEnd) {
            const dayEnd = endOfDay(cur);
            const slotEnd = new Date(Math.min(dayEnd.getTime(), clampedEnd.getTime()));
            const minutes = Math.max(0, (slotEnd - cur) / 60000);
            parts.push({ date: toYMD(cur), minutes });
            cur = new Date(slotEnd.getTime() + 1);
        }
        return parts;
    }

    // Aggregate; zero-fill; target
    const { series, targetSeries, kpi } = useMemo(() => {
        const ok = Array.isArray(bookings) ? bookings : [];
        const valid = ok.filter((b) => (b.status || "") !== "canceled");

        const minutesByKey = new Map();
        let totalMinutes = 0;

        for (const b of valid) {
            const parts = splitBookingByDay(b.start, b.end);
            for (const p of parts) {
                const d = new Date(p.date);
                let key = p.date;
                if (granularity === "week") key = isoWeek(d);
                if (granularity === "month") key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                minutesByKey.set(key, (minutesByKey.get(key) || 0) + p.minutes);
                totalMinutes += p.minutes;
            }
        }

        const keys = buildRangeKeys(granularity, start, end);

        const hoursSeries = keys.map((k) => {
            const hours = +(((minutesByKey.get(k) || 0) / 60)).toFixed(2);
            const revenue = +(hours * assumptions.pricePerHour).toFixed(2);
            return { key: k, hours, revenue };
        });

        const targetSeries = keys.map((k) => {
            const { s, e } = periodBoundsForKey(k, granularity);
            const daysInPeriod = Math.max(1, Math.round((endOfDay(e) - startOfDay(s)) / 86400000) + 1);
            const seatHours = assumptions.pcs * assumptions.hoursOpenPerDay * daysInPeriod * assumptions.occupancyTarget;
            const targetRevenue = +(seatHours * assumptions.pricePerHour).toFixed(2);
            return { key: k, seatHours: Math.round(seatHours), targetRevenue };
        });

        const totalHours = +(totalMinutes / 60).toFixed(2);
        const gross = +(totalHours * assumptions.pricePerHour).toFixed(2);
        const msRange = endOfDay(end) - startOfDay(start);
        const daysRange = Math.max(1, Math.round(msRange / 86400000) + 1);
        const capacitySeatHours = assumptions.pcs * assumptions.hoursOpenPerDay * daysRange;
        const utilization = capacitySeatHours ? +(totalHours / capacitySeatHours).toFixed(3) : 0;

        return { series: hoursSeries, targetSeries, kpi: { totalHours, gross, capacitySeatHours, utilization } };
    }, [
        bookings,
        granularity,
        assumptions.pricePerHour,
        assumptions.pcs,
        assumptions.hoursOpenPerDay,
        assumptions.occupancyTarget,
        start,
        end,
    ]);

    const projected = useMemo(() => {
        const daysRange = Math.max(1, Math.round((endOfDay(end) - startOfDay(start)) / 86400000) + 1);
        const seatHours = assumptions.pcs * assumptions.hoursOpenPerDay * daysRange * assumptions.occupancyTarget;
        const gross = seatHours * assumptions.pricePerHour + seatHours * assumptions.snackARPU;
        const net = gross - assumptions.monthlyFixedCosts * (daysRange / assumptions.daysPerMonth);
        return { seatHours: Math.round(seatHours), gross: +gross.toFixed(2), net: +net.toFixed(2), daysRange };
    }, [assumptions, start, end]);

    // Export CSV
    function exportCSV() {
        const rows = [[granularity, "hours", "revenue", "targetRevenue"]];
        for (let i = 0; i < series.length; i++) {
            const k = series[i].key;
            const s = series[i];
            const t = targetSeries[i];
            rows.push([k, s.hours, s.revenue, t?.targetRevenue ?? ""]);
        }
        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `booked_${granularity}_${toYMD(new Date())}.csv`; a.click(); URL.revokeObjectURL(url);
    }

    return (
        <>
            <AdminHeader />
            <div className="flex flex-col md:flex-row">
                {/* Left nav */}
                <div className="w-full md:w-48 bg-black border-b md:border-b-0 p-4">
                    <AdminSidebar />
                </div>

                {/* Main */}
                <div className="flex-1 p-4">
                    <div className="max-w-7xl mx-auto text-white">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <div>
                                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#a9ffa2]/70">
                                    <span>RGC // Admin Ops Console</span>
                                </div>
                                <h1 className="mt-2 text-3xl md:text-5xl font-extrabold">
                                    Revenue <span className="text-accent">Control</span> Room
                                </h1>
                            </div>
                            {errMsg && (
                                <div className="ml-auto text-xs px-2 py-1 rounded bg-red-500/10 border border-red-500/40 text-red-300">
                                    {errMsg}
                                </div>
                            )}
                        </div>

                        {/* Controls */}
                        <div className="grid gap-3 mb-4 lg:grid-cols-4">
                            <NeonCard title="Date Range">
                                <div className="flex flex-wrap gap-2 items-stretch">
                                    <div className="min-w-0 flex-1">
                                        <input
                                            type="date"
                                            value={toYMD(start)}
                                            onChange={(e) => setStart(new Date(e.target.value))}
                                            className="w-full px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white"
                                        />
                                    </div>
                                    <span className="self-center text-gray-500">→</span>
                                    <div className="min-w-0 flex-1">
                                        <input
                                            type="date"
                                            value={toYMD(end)}
                                            onChange={(e) => setEnd(new Date(e.target.value))}
                                            className="w-full px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white"
                                        />
                                    </div>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button onClick={setThisWeek} className="text-xs px-2 py-1 rounded border border-[#D4FF00]/40 hover:bg-[#D4FF00]/10">This Week</button>
                                    <button onClick={setNextWeek} className="text-xs px-2 py-1 rounded border border-[#D4FF00]/40 hover:bg-[#D4FF00]/10">Next Week</button>
                                    <button onClick={setNext7Days} className="text-xs px-2 py-1 rounded border border-[#D4FF00]/40 hover:bg-[#D4FF00]/10">Next 7 Days</button>
                                    <button onClick={setNextMonth} className="text-xs px-2 py-1 rounded border border-[#D4FF00]/40 hover:bg-[#D4FF00]/10">Next Month</button>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <input type="radio" name="g" checked={granularity === "day"} onChange={() => setGranularity("day")} />
                                        <span>Day</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <input type="radio" name="g" checked={granularity === "week"} onChange={() => setGranularity("week")} />
                                        <span>Week</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <input type="radio" name="g" checked={granularity === "month"} onChange={() => setGranularity("month")} />
                                        <span>Month</span>
                                    </div>

                                    <div className="ml-auto flex items-center gap-2">
                                        <span>Metric:</span>
                                        <label className="flex items-center gap-1">
                                            <input type="radio" name="m" checked={metric === "hours"} onChange={() => setMetric("hours")} /> Hours
                                        </label>
                                        <label className="flex items-center gap-1">
                                            <input type="radio" name="m" checked={metric === "revenue"} onChange={() => setMetric("revenue")} /> Revenue
                                        </label>
                                    </div>
                                </div>
                            </NeonCard>

                            <NeonCard title="Rate & Capacity">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <label className="flex flex-col gap-1">
                                        <span className="text-gray-400">Price / hr ($)</span>
                                        <input type="number" step={0.5} min={0} value={assumptions.pricePerHour}
                                            onChange={(e) => setAssumptions((p) => ({ ...p, pricePerHour: parseFloat(e.target.value) || 0 }))}
                                            className="px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white" />
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span className="text-gray-400">PCs</span>
                                        <input type="number" step={1} min={1} value={assumptions.pcs}
                                            onChange={(e) => setAssumptions((p) => ({ ...p, pcs: parseInt(e.target.value || "0", 10) }))}
                                            className="px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white" />
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span className="text-gray-400">Hours open / day</span>
                                        <input type="number" step={1} min={1} value={assumptions.hoursOpenPerDay}
                                            onChange={(e) => setAssumptions((p) => ({ ...p, hoursOpenPerDay: parseInt(e.target.value || "0", 10) }))}
                                            className="px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white" />
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span className="text-gray-400">Occupancy target</span>
                                        <input type="number" step={5} min={0} max={100} value={Math.round(assumptions.occupancyTarget * 100)}
                                            onChange={(e) => setAssumptions((p) => ({ ...p, occupancyTarget: (parseFloat(e.target.value) || 0) / 100 }))}
                                            className="px-3 py-2 rounded-lg bg-neutral-900 border border-[#D4FF00]/30 text-white" />
                                    </label>
                                </div>
                            </NeonCard>

                            <NeonCard title="Export">
                                <button onClick={exportCSV} className="w-full px-3 py-2 rounded-lg bg-[#D4FF00] text-black font-semibold hover:brightness-90">
                                    Download CSV
                                </button>
                            </NeonCard>

                            <NeonCard title="Live Status">
                                <div className="text-sm text-gray-300">
                                    {loading ? "Loading…" : `Loaded ${bookings.length} bookings${usingFallback ? " (top-level)" : ""}`}
                                </div>
                            </NeonCard>
                        </div>

                        {/* KPIs */}
                        <div className="grid md:grid-cols-4 gap-3 mb-6">
                            <Stat label="Booked hours" value={kpi.totalHours.toLocaleString()} sub={`${toYMD(start)} → ${toYMD(end)}`} />
                            <Stat label="Gross (booked × $/hr)" value={`$${kpi.gross.toLocaleString()}`} />
                            <Stat label="Capacity seat-hours" value={kpi.capacitySeatHours.toLocaleString()} />
                            <Stat label="Utilization" value={`${(kpi.utilization * 100).toFixed(1)}%`} />
                        </div>

                        {/* Main chart */}
                        <NeonCard title={`${metric === "hours" ? "Booked Hours" : "Booked Revenue"} by ${granularity}`}
                            right={<div className="text-xs text-gray-400">Future periods appear even if 0</div>}>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={series} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
                                        <defs>
                                            <linearGradient id="fillA" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={NEON} stopOpacity={0.8} />
                                                <stop offset="95%" stopColor={NEON} stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                        <XAxis dataKey="key" stroke="#bbb" fontSize={12} />
                                        <YAxis stroke="#bbb" fontSize={12} tickFormatter={(v) => (metric === "hours" ? v : `$${v}`)} />
                                        <Tooltip
                                            formatter={(v) => (metric === "hours" ? v : `$${v}`)}
                                            labelFormatter={(l) => `${granularity.toUpperCase()}: ${l}`}
                                            contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(212,255,0,0.3)" }}
                                        />
                                        <Area type="monotone" dataKey={metric === "hours" ? "hours" : "revenue"} stroke={NEON} fill="url(#fillA)" strokeWidth={2} />
                                        {metric === "revenue" && granularity !== "day" && (
                                            <Line
                                                type="monotone"
                                                dataKey={(d) => {
                                                    // map via same key
                                                    return (targetSeries.find((x) => x.key === d.key)?.targetRevenue) || 0;
                                                }}
                                                name="Target (forecast)"
                                                stroke="#ffffff"
                                                strokeDasharray="4 4"
                                                dot={false}
                                            />
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </NeonCard>

                        {/* Projection summary */}
                        <div className="grid md:grid-cols-2 gap-4 my-6">
                            <NeonCard title="What-if: Occupancy vs Revenue (whole range)">
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={Array.from({ length: 17 }, (_, i) => {
                                                const occ = 0.2 + i * 0.05; // 20%..100%
                                                const daysRange = Math.max(1, Math.round((endOfDay(end) - startOfDay(start)) / 86400000) + 1);
                                                const seatHours = assumptions.pcs * assumptions.hoursOpenPerDay * daysRange * occ;
                                                const gross = seatHours * assumptions.pricePerHour + seatHours * assumptions.snackARPU;
                                                const net = gross - assumptions.monthlyFixedCosts * (daysRange / assumptions.daysPerMonth);
                                                return { occupancy: Math.round(occ * 100), gross: +gross.toFixed(2), net: +net.toFixed(2) };
                                            })}
                                            margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                            <XAxis dataKey="occupancy" stroke="#bbb" fontSize={12} tickFormatter={(v) => `${v}%`} />
                                            <YAxis stroke="#bbb" fontSize={12} tickFormatter={(v) => `$${v}`} />
                                            <Tooltip formatter={(v) => `$${v}`} labelFormatter={(l) => `Occupancy ${l}%`} contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(212,255,0,0.3)" }} />
                                            <Line type="monotone" dataKey="gross" stroke={NEON} strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </NeonCard>

                            <NeonCard title="Projected Summary">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Stat label="Projected seat-hours" value={projected.seatHours.toLocaleString()} />
                                    <Stat label="Projected gross" value={`$${projected.gross.toLocaleString()}`} />
                                    <Stat label="Net after fixed" value={`$${projected.net.toLocaleString()}`} />
                                    <Stat label="Range days" value={projected.daysRange} />
                                </div>
                            </NeonCard>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
